import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardStats } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BrandedHeader from "@/components/BrandedHeader";
import { Brain, Briefcase, Users, FileText, BarChart3, TrendingUp, ArrowLeft, ChevronRight, Activity, ClipboardCheck, LogOut, User, Settings, Radio } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";

const NAV_ITEMS = [
  { label: "Dashboard", path: "/admin", icon: BarChart3 },
  { label: "Tests", path: "/admin/tests", icon: ClipboardCheck },
  { label: "Jobs", path: "/admin/jobs", icon: Briefcase },
  { label: "Sessions", path: "/admin/sessions", icon: Users },
  { label: "Proctor Live", path: "/admin/proctor-live", icon: Radio },
];

export function AdminSideNav({ active }) {
  const navigate = useNavigate();
  const { admin, logout, isSuperAdmin } = useAuth();
  
  return (
    <div className="w-[240px] border-r border-border flex-shrink-0 hidden lg:flex flex-col py-4">
      <div className="px-4 mb-6">
        <img 
          src="/branding/securado-logo-white.png" 
          alt="Securado" 
          className="h-8 w-auto object-contain"
        />
        <p className="text-xs text-muted-foreground mt-1">Admin Portal</p>
      </div>
      <nav className="space-y-1 px-2">
        {NAV_ITEMS.map(item => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              active === item.label ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </button>
        ))}
        {isSuperAdmin && (
          <button
            onClick={() => navigate('/admin/settings')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              active === 'Settings' ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        )}
      </nav>
      <div className="mt-auto px-2 space-y-1">
        {/* Admin info */}
        <div className="px-3 py-2 mb-2">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground truncate">{admin?.email}</span>
          </div>
          {isSuperAdmin && (
            <Badge variant="secondary" className="text-xs">Super Admin</Badge>
          )}
        </div>
        
        <button 
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
        <button 
          onClick={() => navigate('/')} 
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await getDashboardStats();
      setStats(res.data);
    } catch (e) {
      console.error("Failed to load stats", e);
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    { label: "Total Jobs", value: stats.total_jobs, icon: Briefcase, color: "text-primary" },
    { label: "Total Sessions", value: stats.total_sessions, icon: Users, color: "text-[hsl(var(--chart-2))]" },
    { label: "Active Sessions", value: stats.active_sessions, icon: Activity, color: "text-[hsl(var(--warning))]" },
    { label: "Reports Generated", value: stats.total_reports, icon: FileText, color: "text-[hsl(var(--chart-4))]" },
  ] : [];

  const recData = stats?.recommendations ? [
    { name: "Advance", value: stats.recommendations.advance, color: "hsl(158 64% 45%)" },
    { name: "Hold", value: stats.recommendations.hold, color: "hsl(38 92% 55%)" },
    { name: "Reject", value: stats.recommendations.reject, color: "hsl(0 72% 52%)" },
  ] : [];

  const radarData = stats?.avg_scores ? Object.entries(stats.avg_scores)
    .filter(([key]) => key !== 'overall')
    .map(([key, value]) => ({
      dimension: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace('Score', '').trim(),
      shortName: key.split('_').map(w => w[0].toUpperCase()).join(''),
      value: value || 0,
      fullMark: 100,
    })) : [];

  return (
    <div className="min-h-screen bg-background flex">
      <AdminSideNav active="Dashboard" />
      
      {/* Header */}
      <div className="flex-1 flex flex-col">
        <BrandedHeader className="lg:hidden">
          <h1 className="text-lg font-display font-semibold text-center">Admin Dashboard</h1>
        </BrandedHeader>

        <div className="px-4 sm:px-6 lg:px-8 py-6 flex-1">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-display font-semibold">Dashboard</h1>
              <p className="text-muted-foreground mt-1">Overview of interview activity and performance</p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-primary typing-dot" />
                  <div className="w-2 h-2 rounded-full bg-primary typing-dot" />
                  <div className="w-2 h-2 rounded-full bg-primary typing-dot" />
                </div>
              </div>
            ) : (
              <>
                {/* Stat Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-dashboard-stats">
                  {statCards.map((stat, i) => (
                    <Card key={i} className="border-border bg-card card-hover">
                      <CardContent className="p-4 sm:p-5">
                        <div className="flex items-center justify-between mb-3">
                          <stat.icon className={`w-5 h-5 ${stat.color}`} />
                          <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <p className="text-2xl sm:text-3xl font-display font-semibold tabular-nums">{stat.value}</p>
                        <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Charts Row */}
                <div className="grid lg:grid-cols-2 gap-6 mb-8">
                  {/* Recommendation Distribution */}
                  <Card className="border-border bg-card" data-testid="admin-dashboard-recommendation-chart">
                    <CardHeader className="p-5">
                      <CardTitle className="text-base font-display">Recommendation Distribution</CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                      {recData.some(d => d.value > 0) ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={recData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 28% 18%)" />
                            <XAxis dataKey="name" tick={{ fill: 'hsl(215 20% 70%)', fontSize: 12 }} axisLine={{ stroke: 'hsl(222 28% 18%)' }} />
                            <YAxis tick={{ fill: 'hsl(215 20% 70%)', fontSize: 12 }} axisLine={{ stroke: 'hsl(222 28% 18%)' }} />
                            <Tooltip
                              contentStyle={{ background: 'hsl(222 47% 8%)', border: '1px solid hsl(222 28% 18%)', borderRadius: '8px', color: 'hsl(210 40% 98%)' }}
                            />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                              {recData.map((entry, idx) => (
                                <Cell key={idx} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
                          No reports generated yet
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Average Scores Radar */}
                  <Card className="border-border bg-card">
                    <CardHeader className="p-5">
                      <CardTitle className="text-base font-display">Average Score Dimensions</CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                      {radarData.length > 0 && radarData.some(d => d.value > 0) ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                            <PolarGrid stroke="hsl(222 28% 18%)" />
                            <PolarAngleAxis dataKey="shortName" tick={{ fill: 'hsl(215 20% 70%)', fontSize: 10 }} />
                            <Radar name="Score" dataKey="value" stroke="hsl(199 89% 55%)" fill="hsl(199 89% 55%)" fillOpacity={0.18} strokeWidth={2} />
                          </RadarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
                          No score data available
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Sessions */}
                <Card className="border-border bg-card">
                  <CardHeader className="p-5 flex flex-row items-center justify-between">
                    <CardTitle className="text-base font-display">Recent Sessions</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/admin/sessions')}>
                      View All <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </CardHeader>
                  <CardContent className="px-5 pb-5">
                    {stats?.recent_sessions?.length > 0 ? (
                      <div className="space-y-2">
                        {stats.recent_sessions.slice(0, 5).map(s => (
                          <div
                            key={s.id}
                            className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-secondary/40 cursor-pointer transition-colors"
                            onClick={() => s.status === 'completed' ? navigate(`/admin/reports/${s.id}`) : null}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${
                                s.status === 'active' ? 'bg-[hsl(var(--success))]' : 'bg-muted-foreground'
                              }`} />
                              <div>
                                <p className="text-sm font-medium">{s.candidate_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {s.started_at ? new Date(s.started_at).toLocaleDateString() : ''}
                                </p>
                              </div>
                            </div>
                            <Badge variant="secondary" className={`text-xs ${
                              s.status === 'active' ? 'text-[hsl(var(--success))]' :
                              s.status === 'completed' ? 'text-primary' : ''
                            }`}>
                              {s.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">No sessions yet</p>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
